import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import { Check, X, ArrowLeft, Star } from 'lucide-react';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

// Package data organized by tier
const TIERS = {
  starter: {
    subtitle: 'FOR NON-VAT COMPANIES',
    title: 'Starter — Compiled Annual Financial Statements',
    description: 'Designed for newly registered or small companies that are not VAT-registered and require Compiled Annual Financial Statements.',
    highlight: 'Ideal for couple-owned and partner-led businesses.',
    packages: [
      {
        name: 'Bronze',
        tier: 'STARTER',
        description: 'Ideal for owner-operated startups still building monthly volume.',
        monthlyPrice: 950,
        annualPrice: 11400,
        popular: false,
        features: {
          vatRegistered: 'No',
          financialStatements: 'Compiled',
          companyTaxReturns: true,
          specialist: 'Bookkeeper',
          workload: 'TCA does 20%',
          payroll: '2 staff included',
          advisoryMeetings: 'Upon request (billed)',
          managementAccounts: 'Quarterly'
        }
      },
      {
        name: 'Silver',
        tier: 'STARTER',
        description: 'Most-loved Starter package — TCA handles the bulk of accounting.',
        monthlyPrice: 1450,
        annualPrice: 17400,
        popular: true,
        features: {
          vatRegistered: 'No',
          financialStatements: 'Compiled',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 50%',
          payroll: '5 staff included',
          advisoryMeetings: 'Upon request (billed)',
          managementAccounts: 'Quarterly'
        }
      },
      {
        name: 'Gold',
        tier: 'STARTER',
        description: 'TCA runs your books end-to-end while you focus on growth.',
        monthlyPrice: 1995,
        annualPrice: 23940,
        popular: false,
        features: {
          vatRegistered: 'No',
          financialStatements: 'Compiled',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 80%',
          payroll: '10 staff included',
          advisoryMeetings: 'Every 2 months',
          managementAccounts: 'Every 2 months'
        }
      }
    ],
    comparison: {
      header: 'INCLUDED IN STARTER',
      packageEssentials: {
        'Annual Financial Statements': ['Compiled', 'Compiled', 'Compiled'],
        'Company Tax Returns': [true, true, true],
        'Specialist': ['Bookkeeper', 'Accountant', 'Accountant'],
        'VAT-Registered Company': [false, false, false],
        'Accounting work handled by TCA': ['TCA does 20%', 'TCA does 50%', 'TCA does 80%'],
        'Payroll + SARS submissions': ['2 staff included', '5 staff included', '10 staff included'],
        'Advisory Meetings': ['Upon request (billed)', 'Upon request (billed)', 'Every 2 months'],
        'Management Accounts': ['Quarterly', 'Quarterly', 'Every 2 months']
      },
      tcaDifference: {
        'Designated Qualified Bookkeeper': [true, false, false],
        'Designated SAIPA/SAICA Accountant': [false, true, true],
        'Designated Tax & Financial Specialist': [true, true, true],
        'Business Profit Reviews & Tax Savings': [true, true, true],
        'A.I. Process Automations (Zapier + ChatGPT)': [true, true, true],
        'Dedicated WhatsApp Support Group': [true, true, true]
      },
      software: {
        'WhatsApp Photos of Slips / Expenses': [true, true, true],
        'Live Accounting / Business Reports': [true, true, true],
        'World-Leading Cloud Accounting (Xero)': [true, true, true],
        'Tailored Xero Training for Staff': [true, true, true]
      },
      compliance: {
        'BEE Affidavit Certificates': [true, true, true],
        'CIPC Consultation & Guidance': [true, true, true],
        'Tax Returns + Provisional Tax Returns': [true, true, true],
        'VAT Return Review + SARS Submission': [false, false, false]
      },
      bookkeeping: {
        'Preparing Sales Invoices': [false, false, true],
        'Posting Bank Transactions': [false, true, true],
        'Posting Expense / Supplier Bills': [false, false, true]
      }
    }
  },
  core: {
    subtitle: 'FOR VAT-REGISTERED COMPANIES',
    title: 'Core — Reviewed Annual Financial Statements',
    description: 'Built for VAT-registered companies who require Reviewed Financial Statements under ISRE 2400 and structured monthly accounting.',
    highlight: 'Our most popular package. 76% of our clients choose the Core option.',
    packages: [
      {
        name: 'Bronze',
        tier: 'CORE',
        description: 'VAT-registered SMEs sharing the bookkeeping load with TCA.',
        monthlyPrice: 1950,
        annualPrice: 23400,
        popular: false,
        features: {
          vatRegistered: 'Yes',
          financialStatements: 'Reviewed',
          companyTaxReturns: true,
          specialist: 'Bookkeeper',
          workload: 'TCA does 20%',
          payroll: '5 staff included',
          advisoryMeetings: 'Upon request (billed)',
          managementAccounts: 'Quarterly'
        }
      },
      {
        name: 'Silver',
        tier: 'CORE',
        description: 'Recommended Core tier — full reviewed AFS and faster reporting.',
        monthlyPrice: 2950,
        annualPrice: 35400,
        popular: true,
        features: {
          vatRegistered: 'Yes',
          financialStatements: 'Reviewed',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 50%',
          payroll: '20 staff included',
          advisoryMeetings: 'Every 2 months',
          managementAccounts: 'Every 2 months'
        }
      },
      {
        name: 'Gold',
        tier: 'CORE',
        description: 'TCA-led accounting for established VAT vendors with bigger payrolls.',
        monthlyPrice: 4650,
        annualPrice: 55800,
        popular: false,
        features: {
          vatRegistered: 'Yes',
          financialStatements: 'Reviewed',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 80%',
          payroll: '40 staff included',
          advisoryMeetings: 'Monthly',
          managementAccounts: 'Every 2 months'
        }
      }
    ],
    comparison: {
      header: 'INCLUDED IN CORE',
      packageEssentials: {
        'Annual Financial Statements': ['Reviewed', 'Reviewed', 'Reviewed'],
        'Company Tax Returns': [true, true, true],
        'Specialist': ['Bookkeeper', 'Accountant', 'Accountant'],
        'VAT-Registered Company': [true, true, true],
        'Accounting work handled by TCA': ['TCA does 20%', 'TCA does 50%', 'TCA does 80%'],
        'Payroll + SARS submissions': ['5 staff included', '20 staff included', '40 staff included'],
        'Advisory Meetings': ['Upon request (billed)', 'Every 2 months', 'Monthly'],
        'Management Accounts': ['Quarterly', 'Every 2 months', 'Every 2 months']
      },
      tcaDifference: {
        'Designated Qualified Bookkeeper': [true, false, false],
        'Designated SAIPA/SAICA Accountant': [false, true, true],
        'Designated Tax & Financial Specialist': [true, true, true],
        'Business Profit Reviews & Tax Savings': [true, true, true],
        'A.I. Process Automations (Zapier + ChatGPT)': [true, true, true],
        'Dedicated WhatsApp Support Group': [true, true, true]
      },
      software: {
        'WhatsApp Photos of Slips / Expenses': [true, true, true],
        'Live Accounting / Business Reports': [true, true, true],
        'World-Leading Cloud Accounting (Xero)': [true, true, true],
        'Tailored Xero Training for Staff': [true, true, true]
      },
      compliance: {
        'BEE Affidavit Certificates': [true, true, true],
        'CIPC Consultation & Guidance': [true, true, true],
        'Tax Returns + Provisional Tax Returns': [true, true, true],
        'VAT Return Review + SARS Submission': [true, true, true]
      },
      bookkeeping: {
        'Preparing Sales Invoices': [false, false, true],
        'Posting Bank Transactions': [false, true, true],
        'Posting Expense / Supplier Bills': [false, false, true]
      }
    }
  },
  elite: {
    subtitle: 'AUDIT-READY BUSINESSES',
    title: 'Elite — Audited Annual Financial Statements',
    description: 'For Non-VAT or VAT-registered companies whose Public Interest Score, MOI or stakeholders require Audited Annual Financial Statements.',
    highlight: 'Ideal for estate agents requiring mandatory PPRA audits, public companies, and body corporates governed by statutory audit requirements. It is also the perfect fit for businesses fulfilling bank covenants, or multi-shareholder companies seeking the sacrosanct peace of mind that an audit brings.',
    packages: [
      {
        name: 'Bronze',
        tier: 'ELITE',
        description: 'Audit-ready businesses that handle their own day-to-day capture.',
        monthlyPrice: 2650,
        annualPrice: 31800,
        popular: false,
        features: {
          vatRegistered: 'No',
          financialStatements: 'Audited',
          companyTaxReturns: true,
          specialist: 'Bookkeeper',
          workload: 'TCA does 20%',
          payroll: '15 staff included',
          advisoryMeetings: 'Upon request (billed)',
          managementAccounts: 'Quarterly'
        }
      },
      {
        name: 'Silver',
        tier: 'ELITE',
        description: 'Mid-market favourite — fully reconciled, audit-ready monthly.',
        monthlyPrice: 4650,
        annualPrice: 55800,
        popular: true,
        features: {
          vatRegistered: 'Yes',
          financialStatements: 'Audited',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 50%',
          payroll: '40 staff included',
          advisoryMeetings: 'Every 2 months',
          managementAccounts: 'Every 2 months'
        }
      },
      {
        name: 'Gold',
        tier: 'ELITE',
        description: 'White-glove monthly accounting for high-PIS regulated entities.',
        monthlyPrice: 7950,
        annualPrice: 95400,
        popular: false,
        features: {
          vatRegistered: 'Yes',
          financialStatements: 'Audited',
          companyTaxReturns: true,
          specialist: 'Accountant',
          workload: 'TCA does 80%',
          payroll: '80 staff included',
          advisoryMeetings: 'Monthly',
          managementAccounts: 'Monthly'
        }
      }
    ],
    comparison: {
      header: 'INCLUDED IN ELITE',
      packageEssentials: {
        'Annual Financial Statements': ['Audited', 'Audited', 'Audited'],
        'Company Tax Returns': [true, true, true],
        'Specialist': ['Bookkeeper', 'Accountant', 'Accountant'],
        'VAT-Registered Company': [false, true, true],
        'Accounting work handled by TCA': ['TCA does 20%', 'TCA does 50%', 'TCA does 80%'],
        'Payroll + SARS submissions': ['15 staff included', '40 staff included', '80 staff included'],
        'Advisory Meetings': ['Upon request (billed)', 'Every 2 months', 'Monthly'],
        'Management Accounts': ['Quarterly', 'Every 2 months', 'Monthly']
      },
      tcaDifference: {
        'Designated Qualified Bookkeeper': [true, false, false],
        'Designated SAIPA/SAICA Accountant': [false, true, true],
        'Designated Tax & Financial Specialist': [true, true, true],
        'Business Profit Reviews & Tax Savings': [true, true, true],
        'A.I. Process Automations (Zapier + ChatGPT)': [true, true, true],
        'Dedicated WhatsApp Support Group': [true, true, true]
      },
      software: {
        'WhatsApp Photos of Slips / Expenses': [true, true, true],
        'Live Accounting / Business Reports': [true, true, true],
        'World-Leading Cloud Accounting (Xero)': [true, true, true],
        'Tailored Xero Training for Staff': [true, true, true]
      },
      compliance: {
        'BEE Affidavit Certificates': [true, true, true],
        'CIPC Consultation & Guidance': [true, true, true],
        'Tax Returns + Provisional Tax Returns': [true, true, true],
        'VAT Return Review + SARS Submission': [false, true, true]
      },
      bookkeeping: {
        'Preparing Sales Invoices': [false, false, true],
        'Posting Bank Transactions': [false, true, true],
        'Posting Expense / Supplier Bills': [false, false, true]
      }
    }
  }
};

const formatPrice = (price) => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Cell renderer for comparison table
const TableCell = ({ value }) => {
  if (value === true) {
    return <Check className="w-5 h-5 text-accent mx-auto" />;
  }
  if (value === false) {
    return <X className="w-5 h-5 text-gray-300 mx-auto" />;
  }
  return <span className="font-semibold text-sm text-foreground">{value}</span>;
};

const PackagesComparison = () => {
  const navigate = useNavigate();
  const [activeTier, setActiveTier] = useState('starter');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentTier = TIERS[activeTier];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo Section */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img
            src={LOGO_URL}
            alt="TCA Auditors Logo"
            className="h-24 sm:h-32 w-auto object-contain select-none"
            draggable="false"
          />
          <img
            src={BANNER_URL}
            alt="Tax & Auditing Consultants - For Businesses & Individuals"
            className="h-[4.5rem] sm:h-24 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-8 sm:mb-10 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top Back Button */}
        <div className="w-full max-w-6xl flex justify-start mb-8">
          <Button
            data-testid="btn-back-to-welcome-top"
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>

        {/* Introduction to Packages */}
        <div className="w-full max-w-6xl mb-10" data-testid="packages-introduction">
          <div className="space-y-4 text-sm sm:text-base text-foreground leading-relaxed">
            <h2 className="text-xl sm:text-2xl font-extrabold text-tca-blue uppercase tracking-wide mb-4">
              HOW OUR RETAINER PACKAGES WORK
            </h2>
            <p>
              We offer 3 (three) levels of services, each of which is comprised of 3 (three) suites of services that we call "packages".
            </p>
            <p>
              The three levels are <strong>Starter</strong>, <strong>Core</strong>, and <strong>Elite</strong>. Each level is then further broken down into <strong>Bronze</strong>, <strong>Silver</strong>, and <strong>Gold</strong>. In total, there are 9 (nine) various packages to choose from.
            </p>

            <div className="mt-4">
              <p className="font-extrabold text-tca-blue uppercase tracking-wide text-sm mb-3">Levels:</p>
              <div className="space-y-3 pl-4">
                <p className="hanging-indent">
                  <span className="font-semibold">— The STARTER level</span> is ideal for <strong className="underline">Non-VAT</strong> Registered companies that are managed by 1–2 owners, have a small staff complement, and don't need reviewed or audited financial statements. Only a Compilation of the financial statements is required.
                </p>
                <p className="hanging-indent">
                  <span className="font-semibold">— The CORE level</span> is ideal for <strong className="underline">VAT</strong>-Registered companies that are managed by 1–4 owners, have a small-to-large staff complement, and are legally required to prepare Reviewed financial statements.
                </p>
                <p className="hanging-indent">
                  <span className="font-semibold">— The ELITE level</span> is ideal for either <strong className="underline">Non-VAT</strong> or <strong className="underline">VAT</strong>-Registered companies that are managed by 1–10 owners, have a small-to-large staff complement, and are legally required to prepare Audited financial statements.
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="font-extrabold text-tca-blue uppercase tracking-wide text-sm mb-3">Packages:</p>
              <div className="space-y-3 pl-4">
                <p className="hanging-indent">
                  <span className="font-semibold">— The BRONZE package</span> is ideal for owner-managed businesses where the owners are hands-on, do the accounting themselves (and don't need a designated accountant from us), and merely require us to "check" the books and postings.
                </p>
                <p className="hanging-indent">
                  <span className="font-semibold">— The SILVER package</span> is ideal for larger businesses where the owners are juggling operations, finances, and accounting at the same time. They manage 70–80% of the accounting themselves but require a designated accountant to assist with 20–30% of the accounting, provide detailed accounting guidance, consulting, system training where necessary, and to "check" the books and postings done by the staff/owners.
                </p>
                <p className="hanging-indent">
                  <span className="font-semibold">— The GOLD package</span> is ideal for large businesses where the owners are juggling clients, operations, and staff/HR at the same time. They wish to outsource either 100% or a large portion of the accounting function to us (leaving accounting to the experts). They require a designated accountant to assist with 80–100% of the accounting, provide detailed accounting guidance, consulting, system training where necessary, and to "check" the books and postings done by the staff/owners (if any).
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Row: Tier Switches + Billing Toggle */}
        <div className="w-full max-w-6xl flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          {/* Tier Switches */}
          <div className="flex border-2 border-foreground rounded-none overflow-hidden" data-testid="tier-switches">
            {['starter', 'core', 'elite'].map((tier) => (
              <button
                key={tier}
                data-testid={`tier-btn-${tier}`}
                onClick={() => setActiveTier(tier)}
                className={`px-6 sm:px-9 py-3 sm:py-4 text-xs sm:text-sm font-extrabold uppercase tracking-[0.18em] transition-colors ${
                  activeTier === tier
                    ? 'bg-foreground text-white'
                    : 'bg-white text-foreground hover:bg-accent hover:text-white'
                }`}
              >
                {tier}
              </button>
            ))}
          </div>

          {/* Monthly / Annual Toggle */}
          <div className="flex border-2 border-foreground rounded-full overflow-hidden" data-testid="billing-toggle">
            <button
              data-testid="billing-btn-monthly"
              onClick={() => setBillingPeriod('monthly')}
              className={`px-5 sm:px-7 py-2 sm:py-3 text-xs sm:text-sm font-extrabold uppercase tracking-[0.14em] transition-colors rounded-full ${
                billingPeriod === 'monthly'
                  ? 'bg-accent text-white'
                  : 'bg-white text-foreground hover:bg-gray-100'
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-btn-annual"
              onClick={() => setBillingPeriod('annual')}
              className={`px-5 sm:px-7 py-2 sm:py-3 text-xs sm:text-sm font-extrabold uppercase tracking-[0.14em] transition-colors rounded-full ${
                billingPeriod === 'annual'
                  ? 'bg-accent text-white'
                  : 'bg-white text-foreground hover:bg-gray-100'
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        {/* Tier Description Box */}
        <div className="w-full max-w-6xl mb-10" data-testid="tier-description-box">
          <div className="bg-orange-50 border-l-4 border-accent p-6 rounded-r-lg">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-accent mb-2">
              {currentTier.subtitle}
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
              {currentTier.title}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {currentTier.description}
              <br />
              <span className="underline font-medium text-foreground">{currentTier.highlight}</span>
            </p>
          </div>
        </div>

        {/* Package Cards */}
        <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-testid="package-cards">
          {currentTier.packages.map((pkg, index) => (
            <div
              key={pkg.name}
              data-testid={`package-card-${pkg.name.toLowerCase()}`}
              className={`relative border rounded-lg p-6 sm:p-8 flex flex-col ${
                pkg.popular
                  ? 'border-2 border-accent shadow-lg'
                  : 'border border-gray-200'
              }`}
            >
              {/* Most Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-4 bg-accent text-white text-[10px] font-extrabold uppercase tracking-[0.18em] px-3 py-1 rounded-sm flex items-center gap-1">
                  <Star className="w-3 h-3 fill-white" />
                  MOST POPULAR
                </div>
              )}

              {/* Tier Label */}
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-muted-foreground mb-1">
                {pkg.tier}
              </p>

              {/* Package Name */}
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
                {pkg.name}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {pkg.description}
              </p>

              {/* Price */}
              <div className="mb-6">
                <span className="text-3xl sm:text-4xl font-extrabold text-foreground">
                  R {formatPrice(billingPeriod === 'monthly' ? pkg.monthlyPrice : pkg.annualPrice)}
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  {billingPeriod === 'monthly' ? 'per month + Xero' : 'per year + Xero'}
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-3 flex-1">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">VAT-registered</span>
                  <span className="text-sm font-semibold">{pkg.features.vatRegistered}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Financial Statements</span>
                  <span className="text-sm font-semibold">{pkg.features.financialStatements}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Company Tax Returns</span>
                  <Check className="w-4 h-4 text-accent" />
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Specialist</span>
                  <span className="text-sm font-semibold">{pkg.features.specialist}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Workload</span>
                  <span className="text-sm font-semibold">{pkg.features.workload}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Payroll</span>
                  <span className="text-sm font-semibold">{pkg.features.payroll}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-muted-foreground">Advisory Meetings</span>
                  <span className="text-sm font-semibold">{pkg.features.advisoryMeetings}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Management Accounts</span>
                  <span className="text-sm font-semibold">{pkg.features.managementAccounts}</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-6">
                <Button
                  data-testid={`btn-get-${activeTier}-${pkg.name.toLowerCase()}`}
                  className={`w-full py-3 h-12 font-semibold text-sm tracking-wide rounded-lg transition-colors ${
                    pkg.popular
                      ? 'bg-accent hover:bg-accent/90 text-white'
                      : 'bg-white border-2 border-foreground text-foreground hover:bg-foreground hover:text-white'
                  }`}
                  onClick={() => window.open(`https://retainer.tcauditors.co.za/${activeTier}-${pkg.name.toLowerCase()}`, '_blank')}
                >
                  Get {pkg.tier.charAt(0) + pkg.tier.slice(1).toLowerCase()} {pkg.name} &rarr;
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Side-by-Side Comparison Table */}
        <div className="w-full max-w-6xl mb-16" data-testid="comparison-section">
          <div className="mb-8">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-muted-foreground mb-2 underline">
              SIDE-BY-SIDE
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-2 font-serif">
              Every feature, lined up.
            </h2>
            <p className="text-sm text-muted-foreground">
              {activeTier.charAt(0).toUpperCase() + activeTier.slice(1)} tier — Bronze, Silver and Gold.
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-left" data-testid="comparison-table">
              {/* Header */}
              <thead>
                <tr className="bg-foreground text-white">
                  <th className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em] w-2/5">
                    {currentTier.comparison.header}
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em] text-center w-1/5">
                    BRONZE
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em] text-center w-1/5">
                    SILVER
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em] text-center w-1/5">
                    GOLD
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Package Essentials */}
                <tr>
                  <td colSpan={4} className="px-4 sm:px-6 py-3 bg-orange-50">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent">
                      PACKAGE ESSENTIALS
                    </span>
                  </td>
                </tr>
                {Object.entries(currentTier.comparison.packageEssentials).map(([feature, values]) => (
                  <tr key={feature} className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-3 text-sm text-foreground">{feature}</td>
                    {values.map((val, i) => (
                      <td key={i} className="px-4 sm:px-6 py-3 text-center">
                        <TableCell value={val} />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* The TCA Difference */}
                <tr>
                  <td colSpan={4} className="px-4 sm:px-6 py-3 bg-orange-50">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent">
                      THE TCA DIFFERENCE
                    </span>
                  </td>
                </tr>
                {Object.entries(currentTier.comparison.tcaDifference).map(([feature, values]) => (
                  <tr key={feature} className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-3 text-sm text-foreground">{feature}</td>
                    {values.map((val, i) => (
                      <td key={i} className="px-4 sm:px-6 py-3 text-center">
                        <TableCell value={val} />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Software */}
                <tr>
                  <td colSpan={4} className="px-4 sm:px-6 py-3 bg-orange-50">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent">
                      SOFTWARE
                    </span>
                  </td>
                </tr>
                {Object.entries(currentTier.comparison.software).map(([feature, values]) => (
                  <tr key={feature} className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-3 text-sm text-foreground">{feature}</td>
                    {values.map((val, i) => (
                      <td key={i} className="px-4 sm:px-6 py-3 text-center">
                        <TableCell value={val} />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Compliance */}
                <tr>
                  <td colSpan={4} className="px-4 sm:px-6 py-3 bg-orange-50">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent">
                      COMPLIANCE
                    </span>
                  </td>
                </tr>
                {Object.entries(currentTier.comparison.compliance).map(([feature, values]) => (
                  <tr key={feature} className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-3 text-sm text-foreground">{feature}</td>
                    {values.map((val, i) => (
                      <td key={i} className="px-4 sm:px-6 py-3 text-center">
                        <TableCell value={val} />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Bookkeeping */}
                <tr>
                  <td colSpan={4} className="px-4 sm:px-6 py-3 bg-orange-50">
                    <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent">
                      BOOKKEEPING
                    </span>
                  </td>
                </tr>
                {Object.entries(currentTier.comparison.bookkeeping).map(([feature, values]) => (
                  <tr key={feature} className="border-b border-gray-100">
                    <td className="px-4 sm:px-6 py-3 text-sm text-foreground">{feature}</td>
                    {values.map((val, i) => (
                      <td key={i} className="px-4 sm:px-6 py-3 text-center">
                        <TableCell value={val} />
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Monthly Price Row */}
                <tr className="bg-foreground text-white">
                  <td className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em]">
                    MONTHLY
                  </td>
                  {currentTier.packages.map((pkg) => (
                    <td key={pkg.name} className="px-4 sm:px-6 py-4 text-center font-bold text-sm">
                      R {formatPrice(pkg.monthlyPrice)}
                    </td>
                  ))}
                </tr>

                {/* Annual Price Row */}
                <tr className="bg-accent text-white">
                  <td className="px-4 sm:px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.18em]">
                    ANNUAL
                  </td>
                  {currentTier.packages.map((pkg) => (
                    <td key={pkg.name} className="px-4 sm:px-6 py-4 text-center font-bold text-sm">
                      R {formatPrice(pkg.annualPrice)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Required Add-On Box */}
        <div className="w-full max-w-6xl mb-10" data-testid="required-addon">
          <div className="border border-gray-200 rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-accent mb-2">
                REQUIRED ADD-ON
              </p>
              <h3 className="text-xl font-bold text-foreground mb-2">
                Xero Cloud Accounting Subscription
              </h3>
              <p className="text-sm text-muted-foreground">
                Required for all packages. Includes the Xero Starter plan licensed via TCA Gold Partner pricing.
              </p>
            </div>
            <div className="text-right shrink-0">
              <span className="text-3xl sm:text-4xl font-extrabold text-foreground">R 500</span>
              <p className="text-xs text-muted-foreground mt-1">per month · R 6 000/year</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="w-full max-w-6xl flex justify-start mt-4 mb-8">
          <Button
            data-testid="btn-back-to-welcome"
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={2} totalPages={3} showTotal={true} pageTitle="Packages Comparison Table" />
        <img
          src={TCA_SHORT_LOGO}
          alt="TCA Logo"
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default PackagesComparison;
